import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { api } from "../../app/Api/ApiSlice";
import { FaFile, FaImage, FaVideo, FaMusic } from "react-icons/fa";

const MAX_FILES = 24;
const SINGLE_FILE_TYPES = ["video", "audio"];

const CreatePost = () => {
    const navigate = useNavigate();

    const [text, setText] = useState("");
    const [audience, setAudience] = useState("public");
    const [files, setFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const fileInputRef = useRef(null);

    const triggerFilePicker = () => {
        fileInputRef.current?.click();
    };

    const detectFileType = (file) => {
        if (file.type.startsWith("image/")) return "image";
        if (file.type.startsWith("video/")) return "video";
        if (file.type.startsWith("audio/")) return "audio";
        return "document";
    };

    const addFiles = (e) => {
        const selected = Array.from(e.target.files);
        e.target.value = ""; // critical reset

        if (files.length + selected.length > MAX_FILES) {
            toast.error(`Maximum ${MAX_FILES} files allowed`);
            return;
        }

        const incoming = selected.map((file) => ({
            file,
            type: detectFileType(file),
            preview: URL.createObjectURL(file),
        }));

        if (files.length === 0) {
            if (SINGLE_FILE_TYPES.includes(incoming[0].type) && incoming.length > 1) {
                toast.error(`Only one ${incoming[0].type} allowed per post`);
                return;
            }
            setFiles(incoming);
            return;
        }

        const dominantType = files[0].type;

        if (incoming.some(f => f.type !== dominantType)) {
            toast.error(`You cannot mix ${dominantType} with other file types`);
            return;
        }

        if (SINGLE_FILE_TYPES.includes(dominantType)) {
            toast.error(`Only one ${dominantType} allowed per post`);
            return;
        }

        setFiles(prev => [...prev, ...incoming]);
    };

    const removeFile = (index) => {
        URL.revokeObjectURL(files[index].preview);
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    useEffect(() => {
        return () => {
            files.forEach(f => URL.revokeObjectURL(f.preview));
        };
    }, [files]);

    const uploadToS3 = async (file) => {
        const presignRes = await api.post("/uploads/presign", {
            filename: file.name,
            contentType: file.type,
        });

        const { uploadUrl, fileUrl, key } = presignRes.data;

        await fetch(uploadUrl, {
            method: "PUT",
            headers: {
                "Content-Type": file.type,
            },
            body: file,
        });

        return {
            url: fileUrl,
            key,
            type: file.type.split("/")[0], // image | video | application
            mimeType: file.type,
            name: file.name,
            size: file.size,
        };
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!text && files.length === 0) {
            toast.error("Post cannot be empty");
            return;
        }

        try {
            setIsLoading(true);

            const uploadedFiles = await Promise.all(
                files.map(async ({ file }) => {
                    const { url, key, type } = await uploadToS3(file);

                    return {
                        url,
                        key,
                        type,
                        name: file.name,
                        size: file.size,
                        mimeType: file.type,
                    };
                })
            );

            // Strip out fields you don’t want
            const postFiles = uploadedFiles.map(({ url, ...others }) => others);

            await api.post("/posts", {
                postText: text,
                audience,
                files: postFiles,
            });


            toast.success("Post created successfully");
            navigate("/");
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "Upload failed");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="">
            <div className="form-wrapper create-form">
                <h3>Create a new post</h3>

                <form onSubmit={handleSubmit}>
                    <textarea
                        type="text"
                        placeholder="Type something"
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        className="post-text"
                    />

                    <div className="select-file-buttons">
                        <button type="button" onClick={triggerFilePicker}><FaImage /></button>
                        <button type="button" onClick={triggerFilePicker}><FaVideo /></button>
                        <button type="button" onClick={triggerFilePicker}><FaMusic /></button>
                        <button type="button" onClick={triggerFilePicker}><FaFile /></button>
                    </div>

                    <input
                        type="file"
                        ref={fileInputRef}
                        hidden
                        multiple
                        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt"
                        onChange={addFiles}
                    />

                    <div className="file-previews">
                        {files.map(({ preview, file, type }, i) => (
                            <div key={i}>
                                {type === "image" && <img src={preview} width={120} />}
                                {type === "video" && <video src={preview} width={180} controls />}
                                {type === "audio" && <audio src={preview} controls />}
                                {type === "document" && <p>{file.name}</p>}
                                <button type="button" onClick={() => removeFile(i)}>Remove</button>
                            </div>
                        ))}
                    </div>

                    <select value={audience} onChange={e => setAudience(e.target.value)}>
                        <option value="me">Me</option>
                        <option value="friends">Friends</option>
                        <option value="public">Public</option>
                    </select>

                    <button type="submit" disabled={isLoading} className="form-btn">
                        {isLoading ? "Uploading..." : "Post"}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default CreatePost;
